import { apiRequest, buildURL, handleApiError } from './commons/CommonService.Functions';
import { getSettlementFiles } from './AzureFileUpload.Services';

// models
import {
    CreditorInvoice,
    CreditorInvoiceData,
    NewCreditorInvoice,
    PaymentInvoiceAllocation,
    PaymentInvoiceAllocationInput,
    SetPaymentInvoicesResult
} from '../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { SettlementFileViewModel } from '../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../models/HttpError';

// functions
import * as Functions from '../functions/common';
import * as Sentry from '@sentry/react';

/**
 * Facturas del profesional y su asignación a pagos (CashFlow, contrato §12.2).
 * Rutas reales de la API detrás del gateway `cashflow/`. Si el endpoint todavía
 * no está publicado responde 404: quien llama lo muestra como "dato no disponible todavía".
 */

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;
const CreditorsURL = () => Gateway + 'cashflow/creditors';
const PaymentsURL = () => Gateway + 'cashflow/payments';

export interface CreditorInvoicesFilter {
    settlementId?: number;
    status?: number;
}

const query = (params: Record<string, string | number | undefined>): string => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') search.append(key, String(value));
    });
    const text = search.toString();
    return text ? `?${text}` : '';
};

const json = (method: string, body?: unknown): RequestInit => ({
    method,
    body: body === undefined ? undefined : JSON.stringify(body)
});

/** GET creditors/{CreditorId}/invoices */
export const GetCreditorInvoices = async (
    token: string,
    creditorId: string,
    filter: CreditorInvoicesFilter = {}
): Promise<CreditorInvoice[]> => {
    const url = buildURL(CreditorsURL(), creditorId, 'invoices') + query({ SettlementId: filter.settlementId, Status: filter.status });
    return (await apiRequest<CreditorInvoice[]>(url, token, 'GetCreditorInvoices')) ?? [];
};

/** POST creditors/{CreditorId}/invoices (upsert por ruta del archivo). */
export const CreateCreditorInvoice = async (token: string, creditorId: string, invoice: NewCreditorInvoice): Promise<CreditorInvoice | undefined> =>
    apiRequest<CreditorInvoice>(buildURL(CreditorsURL(), creditorId, 'invoices'), token, 'CreateCreditorInvoice', json('POST', invoice));

/** PUT creditors/{CreditorId}/invoices/{InvoiceId} (metadatos). 409 si cambia la moneda de una factura con pagos. */
export const UpdateCreditorInvoice = async (
    token: string,
    creditorId: string,
    invoiceId: number,
    data: CreditorInvoiceData
): Promise<CreditorInvoice | undefined> =>
    apiRequest<CreditorInvoice>(buildURL(CreditorsURL(), creditorId, 'invoices', invoiceId), token, 'UpdateCreditorInvoice', json('PUT', data));

/** DELETE creditors/{CreditorId}/invoices/{InvoiceId}[?force=true]. 409 si tiene pagos asociados y no se fuerza. */
export const DeleteCreditorInvoice = async (token: string, creditorId: string, invoiceId: number, force = false): Promise<void> => {
    await apiRequest<unknown>(
        buildURL(CreditorsURL(), creditorId, 'invoices', invoiceId) + (force ? '?force=true' : ''),
        token,
        'DeleteCreditorInvoice',
        json('DELETE')
    );
};

/** GET payments/{PaymentId}/invoices: asignaciones activas con datos de la factura. */
export const GetPaymentInvoices = async (token: string, paymentId: number): Promise<PaymentInvoiceAllocation[]> =>
    (await apiRequest<PaymentInvoiceAllocation[]>(buildURL(PaymentsURL(), paymentId, 'invoices'), token, 'GetPaymentInvoices')) ?? [];

/** Lo que devuelve el PUT puede ser la lista o `{ allocations, warnings }`: se normaliza. */
export const normalizeSetPaymentInvoicesResponse = (body: any): SetPaymentInvoicesResult => {
    const data = body?.data ?? body;
    const allocations: PaymentInvoiceAllocation[] = Array.isArray(data) ? data : Array.isArray(data?.allocations) ? data.allocations : [];
    const rawWarnings = data?.warnings ?? body?.warnings ?? [];
    const warnings = (Array.isArray(rawWarnings) ? rawWarnings : [rawWarnings]).map((item: unknown) => String(item)).filter(Boolean);
    return { allocations, warnings };
};

/** PUT payments/{PaymentId}/invoices: reemplaza el conjunto activo. Devuelve los avisos (sobre-asignación). */
export const SetPaymentInvoices = async (
    token: string,
    paymentId: number,
    allocations: PaymentInvoiceAllocationInput[]
): Promise<SetPaymentInvoicesResult> => {
    const url = buildURL(PaymentsURL(), paymentId, 'invoices');
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ allocations })
        });
        if (!response.ok) {
            Sentry.captureException(response);
            const text = await response.text().catch(() => '');
            throw Functions.HttpErrorHandler(text || null, response);
        }
        const text = await response.text().catch(() => '');
        if (!text) return { allocations: [], warnings: [] };
        let body: any;
        try {
            body = JSON.parse(text);
        } catch {
            return { allocations: [], warnings: [] };
        }
        if (body && body.succeeded === false) {
            throw { ErrorCode: response.status, ErrorMessage: body.message || 'Operation failed', Errors: body.errors ?? [] } as ErrorType;
        }
        return normalizeSetPaymentInvoicesResponse(body);
    } catch (error: any) {
        if (error && typeof error === 'object' && 'ErrorCode' in error) throw error;
        throw await handleApiError(error, 'SetPaymentInvoices');
    }
};

/** Facturas subidas en el detalle de cada liquidación (Settlements `GET settlements/{id}/files`). */
export const GetSettlementsInvoiceFiles = async (
    token: string,
    settlementIds: number[]
): Promise<{ files: SettlementFileViewModel[]; failedSettlementIds: number[] }> => {
    const ids = Array.from(new Set(settlementIds));
    // Una liquidación que falla no impide traer las demás: se informa cuáles fallaron.
    const results = await Promise.allSettled(ids.map((id) => getSettlementFiles(token, id)));
    const files: SettlementFileViewModel[] = [];
    const failedSettlementIds: number[] = [];
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') files.push(...(result.value ?? []));
        else failedSettlementIds.push(ids[index]);
    });
    return { files, failedSettlementIds };
};
